import type { Client, Project } from '@/types';

interface ExpenseFiltersProps {
  categories: string[];
  clients: Client[];
  projects: Project[];
  categoryFilter: string;
  clientFilter: string;
  projectFilter: string;
  recurringFilter: string;
  dateRange: { from: string; to: string };
  onCategoryChange: (category: string) => void;
  onClientChange: (clientId: string) => void;
  onProjectChange: (projectId: string) => void;
  onRecurringChange: (filter: string) => void;
  onDateRangeChange: (range: { from: string; to: string }) => void;
}

export function ExpenseFilters({
  categories,
  clients,
  projects,
  categoryFilter,
  clientFilter,
  projectFilter,
  recurringFilter,
  dateRange,
  onCategoryChange,
  onClientChange,
  onProjectChange,
  onRecurringChange,
  onDateRangeChange,
}: ExpenseFiltersProps) {
  const filteredProjects =
    clientFilter !== 'all' ? projects.filter((p) => p.clientId === clientFilter) : projects;

  return (
    <div className="border border-border rounded-lg p-4 mb-6">
      <h3 className="text-lg font-semibold text-gray-900 mb-4">Filters</h3>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-4">
        <div>
          <label htmlFor="category-filter" className="block text-sm font-medium text-gray-700 mb-1">
            Category
          </label>
          <select
            id="category-filter"
            value={categoryFilter}
            onChange={(e) => onCategoryChange(e.target.value)}
            className="w-full px-3 py-2 rounded-lg border border-gray-300 focus:ring-2 focus:ring-flowky-primary focus:border-transparent"
          >
            <option value="all">All Categories</option>
            {categories.map((category) => (
              <option key={category} value={category}>
                {category}
              </option>
            ))}
          </select>
        </div>

        <div>
          <label htmlFor="client-filter" className="block text-sm font-medium text-gray-700 mb-1">
            Client
          </label>
          <select
            id="client-filter"
            value={clientFilter}
            onChange={(e) => {
              onClientChange(e.target.value);
              if (e.target.value !== clientFilter) {
                onProjectChange('all');
              }
            }}
            className="w-full px-3 py-2 rounded-lg border border-gray-300 focus:ring-2 focus:ring-flowky-primary focus:border-transparent"
          >
            <option value="all">All Clients</option>
            {clients.map((client) => (
              <option key={client.id} value={client.id}>
                {client.name}
              </option>
            ))}
          </select>
        </div>

        <div>
          <label htmlFor="project-filter" className="block text-sm font-medium text-gray-700 mb-1">
            Project
          </label>
          <select
            id="project-filter"
            value={projectFilter}
            onChange={(e) => onProjectChange(e.target.value)}
            className="w-full px-3 py-2 rounded-lg border border-gray-300 focus:ring-2 focus:ring-flowky-primary focus:border-transparent"
          >
            <option value="all">All Projects</option>
            {filteredProjects.map((project) => (
              <option key={project.id} value={project.id}>
                {project.name}
              </option>
            ))}
          </select>
        </div>

        <div>
          <label
            htmlFor="recurring-filter"
            className="block text-sm font-medium text-gray-700 mb-1"
          >
            Type
          </label>
          <select
            id="recurring-filter"
            value={recurringFilter}
            onChange={(e) => onRecurringChange(e.target.value)}
            className="w-full px-3 py-2 rounded-lg border border-gray-300 focus:ring-2 focus:ring-flowky-primary focus:border-transparent"
          >
            <option value="all">All Expenses</option>
            <option value="one-time">One-time</option>
            <option value="recurring">Recurring</option>
          </select>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <label htmlFor="date-from" className="block text-sm font-medium text-gray-700 mb-1">
            From Date
          </label>
          <input
            id="date-from"
            type="date"
            value={dateRange.from}
            onChange={(e) => onDateRangeChange({ ...dateRange, from: e.target.value })}
            className="w-full px-3 py-2 rounded-lg border border-gray-300 focus:ring-2 focus:ring-flowky-primary focus:border-transparent"
          />
        </div>

        <div>
          <label htmlFor="date-to" className="block text-sm font-medium text-gray-700 mb-1">
            To Date
          </label>
          <input
            id="date-to"
            type="date"
            value={dateRange.to}
            onChange={(e) => onDateRangeChange({ ...dateRange, to: e.target.value })}
            className="w-full px-3 py-2 rounded-lg border border-gray-300 focus:ring-2 focus:ring-flowky-primary focus:border-transparent"
          />
        </div>
      </div>

      {(categoryFilter !== 'all' ||
        clientFilter !== 'all' ||
        projectFilter !== 'all' ||
        recurringFilter !== 'all' ||
        dateRange.from ||
        dateRange.to) && (
        <div className="mt-4 pt-4 border-t border-white/30">
          <button
            onClick={() => {
              onCategoryChange('all');
              onClientChange('all');
              onProjectChange('all');
              onRecurringChange('all');
              onDateRangeChange({ from: '', to: '' });
            }}
            className="text-sm text-flowky-primary hover:text-flowky-secondary transition-colors"
          >
            Clear all filters
          </button>
        </div>
      )}
    </div>
  );
}
